// The rounds in flight of the WebGL2 client (R431): the port of
// `render/torpedoes.ts` onto one instance batch.
//
// The public surface is the layer's of before R505, name for name — `apply`,
// `impact`, `count`, `shakeX`, `shakeY` — and
// `impactIsMine` is stated here, because it is a pure test of three ids.
//
// The server owns the flight. This layer interpolates between the 10 Hz
// updates so a round does not stutter, and it reads the run progress off
// the wire (`TorpedoState.run_pct`) — it works out no position, no aim
// point and no speed of its own.
//
// A style is data: `TORPEDO_STYLE_DEFS` comes from `render/torpedo-defs.ts`,
// which carries no renderer at all, and the shapes it names are baked into
// the atlas from the same `paint` the textures of before R505 used.

import { F_SCREEN, outside, type DrawCtx, type FrameInfo, type Layer, type ViewRect } from '../layer';
import { torpedoKey } from '../gfx/atlas';
import type { BatchSource, CircleSink, QuadSink, SegmentSink, SpriteEntry } from './batches';
import { BatchPen } from './monster-pen';
import {
  drawPod, drawPodTrail, newMonsterState, podLateral, type MonsterState,
} from '../../render/monster-bodies';
import { growColumns, grown, moveSlot } from './shared';
import type { TorpedoState } from '../../gen/proto/aquaphobia';
import { TorpedoKind } from '../../gen/proto/aquaphobia';
import { factionColor, MPX_PER_PX } from '../../world';
// `inCubic` is the cubic run of the spark in the final cue: slow, then fast (R2848).
import { clamp01, inCubic, smooth } from '../../render/recipe-kit';
import type { TorpedoStyle } from '../../ui/settings';
import {
  TORPEDO_FORGET_MS, TORPEDO_JOLT_SEC, TORPEDO_SLOWED_ALPHA, TORPEDO_STEEL, TORPEDO_WHITE,
} from '../../gen/presentation';
import {
  CORNER_X, CORNER_Y, WAIT, cueShare, emberAge, flareOf, flareShare, flicker, fusePath,
  fuseShare, runFuse, runTarget, wireTarget,
} from './torpedo-wait';
import {
  runSpeed, TORPEDO_STYLE_DEFS,
  type Mark, type Piece, Pieces, type RoundView, type TorpedoStyleDef,
} from '../../render/torpedo-defs';

/**
 * Whether a torpedo hit is one this viewer is IN, and so whether the camera
 * jolts for it (`torpedoes.ts:45-47`).
 *
 * The blast and its cue belong to everybody watching. The jolt is the felt
 * half of an exchange, and a feeling belongs to the two people having it —
 * the commander who fired and the commander who was hit.
 *
 * `me` is 0 before the hello, and the server sends 0 for `actor` or
 * `subject` where it has nobody to name, so an unattributed hit never jolts.
 *
 * It was copied rather than imported: the module that stated it also built
 * the renderer's textures, and one value import of it would have put the
 * whole library back into this bundle (R431).
 */
export function impactIsMine(me: number, actor: number, subject: number): boolean {
  return me !== 0 && (actor === me || subject === me);
}

/** A round the server stopped sending has hit, run out, or left the
 *  viewport — either way it is gone. */
const FORGET_MS = TORPEDO_FORGET_MS;
/** The impact jolt, from the mockup: two frames of camera shake. */
const JOLT_SEC = TORPEDO_JOLT_SEC;

/**
 * The two looks of the torpedo refresh (R2779, R2783). A round a Torpor
 * slowed in flight (`slowed`) draws at `SLOWED_ALPHA` of
 * `[presentation.torpedo]`. A Stonefish that waits at its aim point draws
 * the square fuse of R2848 (`torpedo-wait.ts`, `[presentation.torpedo_wait]`).
 */
const SLOWED_ALPHA = TORPEDO_SLOWED_ALPHA;

/** How a waiting round ended (R2848): still waiting, a detonation (the
 *  marks go at once) or a round kill (the marks fade). */
export const WAIT_ON = 0, WAIT_BOOM = 1, WAIT_KILL = 2;

/** The wait age a round takes when it is first seen already waiting: it
 *  came into the view mid-wait, so its hand-over is long over. */
const SETTLED_SEC = 1e6;

/** The jolt's two axes (`torpedoes.ts:91-99`). */
const JOLT_X_PX = 3.2, JOLT_X_HZ = 230;
const JOLT_Y_PX = 2.4, JOLT_Y_HZ = 310, JOLT_Y_PHASE = 1.1;

/**
 * Every column of one number a slot, by field name: the list the grow and
 * the swap-remove share (R2212). The shed-mark ledger has `stride` numbers
 * a slot, so the layer grows and moves it itself.
 */
export const TORPEDO_COLUMNS = [
  'id', 'faction', 'heavy', 'kind', 'effect', 'hidden', 'hasStage', 'markLen',
  'x', 'y', 'sx', 'sy', 'heading', 'u', 'su', 'pxSec', 'travel', 'stageX',
  'stageY', 'stageAge', 'seen', 'pod', 'slowed', 'wait', 'waitAge', 'waitFuse',
  'waitTarget', 'wirePct', 'slowAge', 'flareFrom', 'waitEnd', 'endAge',
] as const;

/** The step of a quill pod's trail astern, in world px (round 109: ten
 *  points, 0.03 of a run apart, on a run of about 100 px). */
const POD_TRAIL_STEP_PX = 3;

/** How many rounds the arrays hold before the first doubling. */
const START_CAPACITY = 64;

/** The style a setting names, and Terminal Burn for a name the table lacks. */
function styleDef(style: TorpedoStyle): TorpedoStyleDef {
  return TORPEDO_STYLE_DEFS[style] ?? TORPEDO_STYLE_DEFS['terminal-burn'];
}

/** The floats one round's shed-mark ledger takes: `x, y, travel` a mark. */
function markStride(keep: number): number {
  return Math.max(1, keep) * 3;
}

/**
 * One round in flight, as the effects layer reads it (R910).
 *
 * It is a second reused buffer beside the style's own `RoundView` and not a
 * widening of it: the style draws the round, and the effects layer draws
 * the owner's mark on it, so neither reads the other's fields.
 */
export interface RoundSnap {
  id: number;
  faction: number;
  /** Where the round is drawn now, in world px. */
  x: number; y: number;
  /** The heading in radians. */
  heading: number;
  /** The run progress, 0 to 1. */
  u: number;
  /**
   * How fast the round runs, in world px a second (R924).
   *
   * It is measured off the drawn place and not off a catalog figure, so a
   * mark an effect leaves in the water falls behind at the speed the eye
   * reads. The layer smooths it with the same coefficient the interpolation
   * takes, so one long frame does not spike it.
   */
  speed: number;
  /** The round's kind (`TorpedoKind`). */
  kind: number;
  /** The owner's effect, a catalog wire id; 0 = none. */
  effect: number;
  /** The round and everything it sheds are off the glass. */
  hidden: boolean;
}

export class TorpedoLayer implements Layer {
  readonly name = 'torpedoes';
  private shown = true;

  private readonly quads: QuadSink;
  private readonly sprite: (key: string) => SpriteEntry;
  /**
   * The atlas entry of every shape, looked up on its first draw and kept.
   * The atlas rewrites an entry in place when it grows, so a kept entry
   * samples the right texels after a repack.
   */
  private readonly texEntries = new Map<string, SpriteEntry>();

  private style: TorpedoStyleDef;
  /** How many shed marks the style keeps, which fixes the ring's stride. */
  private keep: number;
  /** The floats one round's ledger takes, `markStride(keep)`. */
  private stride: number;

  private cap = START_CAPACITY;
  private n = 0;
  private idToIndex = new Map<number, number>();

  private id = new Uint32Array(START_CAPACITY);
  private faction = new Uint8Array(START_CAPACITY);
  private heavy = new Uint8Array(START_CAPACITY);
  /** The round's kind (`TorpedoKind`), which an effect row may be scoped to. */
  private kind = new Uint8Array(START_CAPACITY);
  /**
   * The launcher owner's effect on this round, a catalog wire id of
   * `data/contracts/effects.toml`; 0 = none (R910). A round carries no
   * detection filter, so the server states the number with no gate.
   */
  private effect = new Uint32Array(START_CAPACITY);
  /** 1 where the last `advance` packed no piece, because the round, its
   *  marks and its spent stage were all off the glass. */
  private hidden = new Uint8Array(START_CAPACITY);
  private hasStage = new Uint8Array(START_CAPACITY);
  private x = new Float32Array(START_CAPACITY);
  private y = new Float32Array(START_CAPACITY);
  private sx = new Float32Array(START_CAPACITY);
  private sy = new Float32Array(START_CAPACITY);
  private heading = new Float32Array(START_CAPACITY);
  private u = new Float32Array(START_CAPACITY);
  private su = new Float32Array(START_CAPACITY);
  /** The drawn speed, in world px a second, for the effects layer (R924). */
  private pxSec = new Float32Array(START_CAPACITY);
  private travel = new Float32Array(START_CAPACITY);
  private stageX = new Float32Array(START_CAPACITY);
  private stageY = new Float32Array(START_CAPACITY);
  private stageAge = new Float32Array(START_CAPACITY);
  private seen = new Float64Array(START_CAPACITY);
  /** The design of a quill pod (R2719): the Quillback's that launched it,
   *  read once at the first sighting. 0 on every other round. */
  private pod = new Uint8Array(START_CAPACITY);
  /** 1 for a round a Torpor slowed (`TorpedoState.slowed`, R2783). */
  private slowed = new Uint8Array(START_CAPACITY);
  /**
   * 1 while a Stonefish waits at its aim point (R2848). It latches on the
   * first `loiter_pct > 0`: the server rounds the figure, so the last half
   * step of the wait reads 0 while the round still waits, and a round
   * that read a wait once waits until it ends or leaves the view.
   */
  private wait = new Uint8Array(START_CAPACITY);
  /** Wall seconds since the round began to wait (the hand-over clock). */
  private waitAge = new Float32Array(START_CAPACITY);
  /** The share of the wait left that the fuse draws, 0..1 (`runFuse`). */
  private waitFuse = new Float32Array(START_CAPACITY);
  /** The best estimate of the true share of the wait left (`wireTarget`). */
  private waitTarget = new Float32Array(START_CAPACITY);
  /** The last `loiter_pct` off the wire; negative before the first. */
  private wirePct = new Float32Array(START_CAPACITY);
  /** Wall seconds since a Torpor cut the wait; negative for none. */
  private slowAge = new Float32Array(START_CAPACITY);
  /** The share the fuse drew before the cut, burned off in the flare. */
  private flareFrom = new Float32Array(START_CAPACITY);
  /** How the wait ended: `WAIT_ON`, `WAIT_BOOM` or `WAIT_KILL`. */
  private waitEnd = new Uint8Array(START_CAPACITY);
  /** Wall seconds since the wait ended. */
  private endAge = new Float32Array(START_CAPACITY);
  /** The shed marks, oldest first: `x, y, travel` a mark. */
  private marks: Float32Array;
  private markLen = new Uint8Array(START_CAPACITY);

  /**
   * The mark scale (R1981, ui-map-interface.md §6.3): the size of every
   * round and of what it sheds. 1 is the season's picture.
   */
  private markK = 1;

  private joltAge = JOLT_SEC;
  /** The reused view, the reused piece list and the reused mark objects. */
  private view: RoundView = {
    x: 0, y: 0, heading: 0, u: 0, spd: 0, travel: 0, heat: 0, wake: 1,
    heavy: false, color: 0, marks: [], stage: null,
  };
  private readonly pieces = new Pieces();
  /** One mark object a slot, kept for the life of the layer. */
  private markPool: Mark[] = [];
  /** The window on to the pool the style is handed. */
  private markView: Mark[] = [];
  private stageView = { x: 0, y: 0, age: 0 };

  /**
   * The pod design of the Quillback nearest to a point (R2719); the scene
   * answers it from the monster layer. Null draws the first design.
   */
  podDesignAt: ((x: number, y: number) => number) | null = null;

  /** The quill pod's own batches and pen: a pod is no atlas round. */
  private readonly podSegs: SegmentSink;
  private readonly podCircles: CircleSink;
  private readonly podPen: BatchPen;
  private readonly podState: MonsterState = newMonsterState();
  private podClock = 0;
  /** The wall clock of the spark's flicker and the embers, in seconds. */
  private clock = 0;

  /**
   * The wall seconds of a whole wait of a round kind (R2848): the catalog
   * `loiter_sec` over the map clock. The scene answers it from the hello;
   * 0 or null is a wait of unknown length, whose fuse follows the wire
   * figure and draws no cue.
   */
  waitSecOf: ((kind: number) => number) | null = null;

  /** The wait marks (R2848): the fuse and its ash under the rounds, the
   *  spark's dot, the embers and the white core over them. */
  private readonly waitSegs: SegmentSink;
  private readonly waitDots: CircleSink;
  /** The reused path of one fuse: the spark and up to four corners. */
  private readonly fuse = new Float32Array(10);

  constructor(batches: BatchSource, style: TorpedoStyle = 'terminal-burn') {
    this.style = styleDef(style);
    this.keep = this.style.marks ?? 0;
    this.stride = markStride(this.keep);
    this.marks = new Float32Array(START_CAPACITY * this.stride);
    for (let i = 0; i < this.keep; i++) this.markPool.push({ x: 0, y: 0, travel: 0 });
    this.quads = batches.newQuads(256);
    this.sprite = (key) => batches.sprite(key);
    this.podSegs = batches.newSegments(128);
    this.podCircles = batches.newCircles(128);
    this.podPen = new BatchPen(this.podSegs, this.podCircles, null, null);
    this.waitSegs = batches.newSegments(64);
    this.waitDots = batches.newCircles(64);
  }

  /**
   * Sets the mark scale (R1981). A value that is not a positive finite
   * number reads as 1.
   */
  setMarkScale(k: number): void {
    this.markK = Number.isFinite(k) && k > 0 ? k : 1;
  }

  /** The mark scale in force. */
  get markScale(): number { return this.markK; }

  /**
   * Changes the round style while the layer lives (R806).
   *
   * The style fixes the stride of the shed-mark ring, so a change builds a
   * new ring and drops the shed marks and the spent stages of the rounds in
   * flight. The rounds themselves keep flying and shed again in the new
   * style. The same style again is one compare and nothing else.
   */
  setStyle(style: TorpedoStyle): void {
    const def = styleDef(style);
    if (def === this.style) return;
    this.style = def;
    this.keep = def.marks ?? 0;
    this.stride = markStride(this.keep);
    this.marks = new Float32Array(this.cap * this.stride);
    this.markLen.fill(0);
    this.hasStage.fill(0);
    this.stageAge.fill(0);
    for (let i = this.markPool.length; i < this.keep; i++) {
      this.markPool.push({ x: 0, y: 0, travel: 0 });
    }
  }

  get count(): number { return this.n; }
  get visible(): boolean { return this.shown; }
  setVisible(v: boolean): void { this.shown = v; }

  /** The style the layer draws now. A round that goes off draws the
   *  detonation look of the World theme (R2079), which no style scales. */
  get styleDef(): TorpedoStyleDef { return this.style; }

  /** The camera offset of the impact jolt, in screen px. The wiring adds it
   *  to the quake's own offset, and `settings.screenshake` gates both. */
  get shakeX(): number {
    if (this.joltAge >= JOLT_SEC) return 0;
    return JOLT_X_PX * (1 - this.joltAge / JOLT_SEC) * Math.sin(this.joltAge * JOLT_X_HZ);
  }

  get shakeY(): number {
    if (this.joltAge >= JOLT_SEC) return 0;
    return JOLT_Y_PX * (1 - this.joltAge / JOLT_SEC)
      * Math.sin(this.joltAge * JOLT_Y_HZ + JOLT_Y_PHASE);
  }

  /** A round detonated in view. Styles that ask for it jolt the camera. */
  impact(): void {
    if (this.style.jolt) this.joltAge = 0;
  }

  // -------------------------------------------------------------------------
  // The set
  // -------------------------------------------------------------------------

  private grow(): void {
    const cap = this.cap * 2;
    growColumns(this, TORPEDO_COLUMNS, cap);
    this.marks = grown(this.marks, cap, this.stride);
    this.cap = cap;
  }

  private removeAt(i: number): void {
    this.idToIndex.delete(this.id[i]);
    const last = this.n - 1;
    if (i !== last) {
      moveSlot(this, TORPEDO_COLUMNS, i, last);
      const stride = this.stride;
      this.marks.copyWithin(i * stride, last * stride, (last + 1) * stride);
      this.idToIndex.set(this.id[i], i);
    }
    this.n = last;
  }

  /**
   * Drops every round now (R531).
   *
   * A round is normally forgotten `FORGET_MS` after the server stopped
   * mentioning it, because the wire is a stream and a gap is not an end. A
   * replay film that seeks has no stream: the water at the new time holds
   * whatever the log holds, and a round of the old time must not coast on
   * through it.
   */
  clear(): void {
    this.n = 0;
    this.idToIndex.clear();
    this.joltAge = JOLT_SEC;
  }

  apply(list: TorpedoState[]): void {
    const now = performance.now();
    for (const t of list) {
      const x = t.xMpx / MPX_PER_PX, y = t.yMpx / MPX_PER_PX;
      const u = clamp01(t.runPct / 100);
      let i = this.idToIndex.get(t.id);
      if (i === undefined) {
        if (this.n === this.cap) this.grow();
        i = this.n++;
        this.id[i] = t.id;
        this.faction[i] = t.faction;
        this.heavy[i] = t.kind === TorpedoKind.TORPEDO_KIND_HEAVY ? 1 : 0;
        this.kind[i] = t.kind;
        this.pod[i] = t.kind === TorpedoKind.TORPEDO_KIND_POD && this.podDesignAt
          ? this.podDesignAt(x, y) : 0;
        this.hidden[i] = 0;
        this.x[i] = x; this.y[i] = y;
        this.u[i] = u;
        this.pxSec[i] = 0;
        this.travel[i] = 0;
        this.markLen[i] = 0;
        this.hasStage[i] = 0;
        this.stageAge[i] = 0;
        this.slowed[i] = t.slowed ? 1 : 0;
        this.wait[i] = 0;
        this.waitEnd[i] = WAIT_ON;
        // A round first seen already waiting came into the view mid-wait:
        // it skips the hand-over, and a cut it took is long burned out.
        if (t.loiterPct > 0) this.startWait(i, SETTLED_SEC, t.slowed);
        this.idToIndex.set(t.id, i);
      }
      this.sx[i] = x; this.sy[i] = y;
      this.heading[i] = t.headingMrad / 1000;
      this.su[i] = u;
      // The effect may change between two frames of one flight: the owner
      // rejoined with a new pick, and the next state frame states it.
      this.effect[i] = t.effect;
      // The two marks of the refresh can change in one flight: a round
      // reaches its aim point and waits, or a Torpor slows it (R2783).
      if (!this.wait[i] && t.loiterPct > 0) this.startWait(i, 0, t.slowed);
      if (this.wait[i]) this.readWait(i, t.loiterPct, t.slowed);
      this.slowed[i] = t.slowed ? 1 : 0;
      this.seen[i] = now;
    }
  }

  /** Whether the round `id` waits at its aim point (R2848): the latch. */
  isWaiting(id: number): boolean {
    const i = this.idToIndex.get(id);
    return i !== undefined && this.wait[i] === 1;
  }

  /** Latches round `i` as waiting (R2848), `age` wall seconds into its
   *  wait. A round slowed before the wait shows the slowed marks with no
   *  flare. */
  private startWait(i: number, age: number, slowed: boolean): void {
    this.wait[i] = 1;
    this.waitAge[i] = age;
    this.wirePct[i] = -1;
    this.slowAge[i] = slowed ? SETTLED_SEC : -1;
    this.flareFrom[i] = 0;
  }

  /**
   * Reads one wire figure of a waiting round (R2848). A Torpor that slows
   * the round now cut its wait: the fuse drops to the new figure at once,
   * and the flare burns the lost length from the share it drew before.
   */
  private readWait(i: number, pct: number, slowed: boolean): void {
    if (slowed && !this.slowed[i] && this.slowAge[i] < 0) {
      this.flareFrom[i] = this.waitFuse[i];
      this.slowAge[i] = 0;
      this.wirePct[i] = -1;
    }
    const prev = this.wirePct[i];
    this.waitTarget[i] = wireTarget(prev, pct, this.waitTarget[i]);
    if (prev < 0) this.waitFuse[i] = this.waitTarget[i];
    this.wirePct[i] = pct;
  }

  /**
   * A waiting round ended (R2848): `how` is `WAIT_BOOM` for a detonation
   * (the end of the wait or the hull fuse), which takes the round and its
   * marks at once, or `WAIT_KILL` for a round kill, which fades them. The
   * wire event names no round, so the waiting round of `kind` nearest to
   * `x, y` inside `end_match_px` ends. It answers whether one did.
   */
  endWait(how: number, kind: number, x: number, y: number): boolean {
    let best = -1, bestD = WAIT.endMatchPx * WAIT.endMatchPx;
    for (let i = 0; i < this.n; i++) {
      if (!this.wait[i] || this.waitEnd[i] !== WAIT_ON || this.kind[i] !== kind) continue;
      const dx = this.sx[i] - x, dy = this.sy[i] - y, d = dx * dx + dy * dy;
      if (d <= bestD) { best = i; bestD = d; }
    }
    if (best < 0) return false;
    this.waitEnd[best] = how;
    this.endAge[best] = 0;
    return true;
  }

  // -------------------------------------------------------------------------
  // The frame
  // -------------------------------------------------------------------------

  update(frame: FrameInfo): void {
    this.advance(frame.dtMs, frame.cull, frame.cam.zoom);
  }

  /**
   * Advances every round, and packs the pieces of the ones on screen only
   * (R192, §10.7).
   *
   * The flight is never culled: the interpolation, the run progress, the
   * shed marks and the stage clock all keep running, so a round that
   * crosses the screen is in the right place with the right trail behind
   * it. The test asks three points — the round, the oldest mark and the
   * spent stage — because both of those can be in the picture while the
   * round itself is not.
   */
  private advance(deltaMS: number, cull: ViewRect, zoom = 1): void {
    const now = performance.now();
    const dt = Math.min(deltaMS, 100) / 1000;
    const k = 1 - Math.exp(-deltaMS / 70);
    if (this.joltAge < JOLT_SEC) this.joltAge += dt;
    this.podClock += dt;
    this.clock += dt;
    this.zoom = zoom > 0 ? zoom : 1;
    this.podSegs.begin();
    this.podCircles.begin();
    this.waitSegs.begin();
    this.waitDots.begin();

    const shed = this.style.shedPx ?? 0;
    const stageAt = this.style.stageAt;
    const stride = this.stride;
    const { x0, y0, x1, y1 } = cull;
    this.quads.begin();

    for (let i = 0; i < this.n; i++) {
      if (now - this.seen[i] > FORGET_MS) { this.removeAt(i); i--; continue; }
      const px = this.x[i], py = this.y[i];
      this.x[i] += (this.sx[i] - this.x[i]) * k;
      this.y[i] += (this.sy[i] - this.y[i]) * k;
      this.u[i] += (this.su[i] - this.u[i]) * k;
      const moved = Math.hypot(this.x[i] - px, this.y[i] - py);
      this.travel[i] += moved;
      // The speed an effect's mark falls behind at (R924). The frame's own
      // figure is noisy, because the place is interpolated toward the last
      // state frame, so it is smoothed with the same coefficient.
      if (dt > 0) this.pxSec[i] += (moved / dt - this.pxSec[i]) * k;
      if (shed > 0 && this.keep > 0) {
        const len = this.markLen[i];
        const lastTravel = len === 0 ? 0 : this.marks[i * stride + (len - 1) * 3 + 2];
        if (len === 0 || this.travel[i] - lastTravel >= shed) {
          this.pushMark(i, stride);
        }
      }
      if (stageAt !== undefined) {
        if (this.hasStage[i]) this.stageAge[i] += dt;
        else if (this.u[i] >= stageAt) {
          this.hasStage[i] = 1;
          this.stageX[i] = this.x[i]; this.stageY[i] = this.y[i]; this.stageAge[i] = 0;
        }
      }
      // The wait runs whether or not the round is on the glass, so a round
      // that comes into the picture shows the right fuse (R2848).
      let fade = 1;
      if (this.wait[i]) fade = this.runWait(i, dt);
      const len = this.markLen[i];
      if (fade <= 0) { this.hidden[i] = 1; continue; }
      if (outside(this.x[i], this.y[i], x0, y0, x1, y1)
        && (len === 0 || outside(this.marks[i * stride], this.marks[i * stride + 1], x0, y0, x1, y1))
        && (!this.hasStage[i] || outside(this.stageX[i], this.stageY[i], x0, y0, x1, y1))) {
        // The effects layer reads the same answer rather than culling a
        // second time, so one rect test serves both layers (§10.7).
        this.hidden[i] = 1;
        continue;
      }
      this.hidden[i] = 0;
      if (this.kind[i] === TorpedoKind.TORPEDO_KIND_POD) { this.placePod(i); continue; }

      const v = this.view;
      v.x = this.x[i]; v.y = this.y[i]; v.heading = this.heading[i];
      v.u = this.u[i]; v.spd = runSpeed(this.u[i]); v.travel = this.travel[i];
      v.heat = this.u[i]; v.wake = 1;
      v.heavy = this.heavy[i] === 1;
      v.color = factionColor(this.faction[i]);
      v.marks = this.fillMarks(i, stride);
      v.stage = this.fillStage(i);
      if (!this.wait[i]) {
        this.style.pieces(v, this.pieces);
        this.place(i, this.pieces, this.slowed[i] ? SLOWED_ALPHA : 1);
        this.pieces.clear();
        continue;
      }
      // The hand-over at arrival (R2848): the body stops and keeps the
      // dart and the heading of the look, the trail shrinks to its rest
      // state, the bubbles pop in place and the heat cools to the ink.
      const age = this.waitAge[i];
      v.spd *= 1 - smooth(0, WAIT.settleSec, age);
      v.heat *= 1 - smooth(0, WAIT.coolSec, age);
      v.wake = 1 - smooth(0, WAIT.popSec, age);
      this.placeWait(i, v.color, fade);
    }
  }

  /** The zoom of this frame: CSS px for one world px, for the screen
   *  widths and the screen floors of the wait marks. */
  private zoom = 1;

  /**
   * One frame of the wait clock of round `i` (R2848), and the fade of its
   * marks: 1 while it waits, falling to 0 over `kill_fade_sec` after a
   * round kill, and 0 at once after a detonation.
   */
  private runWait(i: number, dt: number): number {
    this.waitAge[i] += dt;
    const sec = this.waitSecOf?.(this.kind[i]) ?? 0;
    const rate = sec > 0 ? 1 / sec : 0;
    this.waitTarget[i] = runTarget(this.waitTarget[i], this.wirePct[i], rate, dt);
    this.waitFuse[i] = runFuse(this.waitFuse[i], this.waitTarget[i], rate, dt);
    if (this.slowAge[i] >= 0 && this.slowAge[i] < SETTLED_SEC) {
      this.slowAge[i] += dt;
      this.flareFrom[i] = Math.max(0, this.flareFrom[i] - rate * dt);
    }
    const end = this.waitEnd[i];
    if (end === WAIT_ON) return 1;
    this.endAge[i] += dt;
    if (end === WAIT_BOOM) return 0;
    return 1 - smooth(0, WAIT.killFadeSec, this.endAge[i]);
  }

  /**
   * Packs one waiting Stonefish (R2848, card 05 Square Fuse of mockup
   * round 133): the fuse left and its ash, the spark with its glow and
   * two embers, the body at rest, and in the final cue the spark's run
   * to the body and the white core. `fade` is the round kill's fade.
   */
  private placeWait(i: number, ink: number, fade: number): void {
    const z = this.zoom;
    const sec = this.waitSecOf?.(this.kind[i]) ?? 0;
    const slowAge = this.slowAge[i];
    const share = flareShare(this.waitFuse[i], this.flareFrom[i], slowAge);
    const f = fuseShare(share, sec), cue = cueShare(share, sec);
    const flare = flareOf(slowAge);
    const dim = slowAge >= WAIT.flareSec;
    const cx = this.x[i], cy = this.y[i];
    const h = this.heading[i], cos = Math.cos(h), sin = Math.sin(h);
    const half = WAIT.sidePx / 2;

    // The ash of the burned part: the whole square, faint, under the fuse.
    const segs = this.waitSegs;
    if (f < 1) {
      for (let s = 0; s < 4; s++) {
        const ax = CORNER_X[s] * half, ay = CORNER_Y[s] * half;
        const bx = CORNER_X[s + 1] * half, by = CORNER_Y[s + 1] * half;
        segs.line(
          cx + ax * cos - ay * sin, cy + ax * sin + ay * cos,
          cx + bx * cos - by * sin, cy + bx * sin + by * cos,
          WAIT.ashWidthPx, ink, WAIT.ashAlpha * fade, F_SCREEN,
        );
      }
    }
    // The fuse left, from the spark on. A slowed round dashes it once the
    // flare has burned out.
    const path = this.fuse, n = fusePath(f, WAIT.sidePx, path);
    const dash = dim ? WAIT.dashPx : 0;
    for (let p = 0; p + 1 < n; p++) {
      const ax = path[p * 2], ay = path[p * 2 + 1], bx = path[p * 2 + 2], by = path[p * 2 + 3];
      segs.line(
        cx + ax * cos - ay * sin, cy + ax * sin + ay * cos,
        cx + bx * cos - by * sin, cy + bx * sin + by * cos,
        WAIT.fuseWidthPx, ink, WAIT.fuseAlpha * fade, F_SCREEN, dash, dash,
      );
    }

    // The spark: on the fuse, or on its run in to the body in the cue.
    let sx = 0, sy = 0, lit = false;
    if (cue > 0) {
      const r = 1 - inCubic(cue);
      sx = half * r; sy = -half * r; lit = true;
    } else if (n > 0) {
      sx = path[0]; sy = path[1]; lit = true;
    }
    lit = lit && this.waitAge[i] > WAIT.sparkDelaySec;
    const t = this.clock + (this.id[i] % 7) * 0.9;
    let wx = 0, wy = 0;
    const spark = dim ? TORPEDO_STEEL : ink;
    if (lit) {
      wx = cx + sx * cos - sy * sin; wy = cy + sx * sin + sy * cos;
      const fl = flicker(t);
      const d = WAIT.sparkGlowPx + WAIT.flareGlowPx * flare;
      const g = this.entry('glow');
      this.quads.quad(wx, wy, 0, 0, d, d, 0, 0, spark,
        (WAIT.sparkGlowAlpha + WAIT.flareGlowAlpha * flare) * fl * fade,
        g.u0, g.v0, g.u1, g.v1);
      const dots = this.waitDots;
      dots.disc(wx, wy, Math.max(WAIT.sparkDotMinPx / z, WAIT.sparkDotPx), TORPEDO_WHITE,
        (dim ? WAIT.slowedDotAlpha : WAIT.sparkDotAlpha) * fl * fade);
      // The embers fall down the screen behind the spark, one to each
      // side by turns.
      const count = WAIT.emberCount, er = Math.max(WAIT.emberDotMinPx / z, WAIT.emberDotPx);
      for (let e = 0; e < count; e++) {
        const a = emberAge(t, e, count);
        dots.disc(wx + (e % 2 ? 1 : -1) * a * WAIT.emberDriftPx, wy + a * WAIT.emberFallPx, er,
          spark, WAIT.emberAlpha * (1 - a) * fade);
      }
    }

    // The body at rest, at full alpha, or dim once a Torpor slowed it.
    const body = (this.slowed[i] ? WAIT.slowedWaitAlpha : 1) * fade;
    this.style.pieces(this.view, this.pieces);
    this.place(i, this.pieces, body);
    this.pieces.clear();
    // The cue's white core, and the white point the body ends as.
    if (cue > 0) {
      const d = WAIT.coreGlowPx + WAIT.coreGrowPx * cue;
      const g = this.entry('glow');
      this.quads.quad(cx + WAIT.coreGlowXPx * cos, cy + WAIT.coreGlowXPx * sin, 0, 0, d, d, 0, 0,
        TORPEDO_WHITE, WAIT.coreGlowAlpha * cue * body, g.u0, g.v0, g.u1, g.v1);
      this.waitDots.disc(cx, cy, Math.max(WAIT.coreDotMinPx / z, WAIT.coreDotPx), TORPEDO_WHITE,
        inCubic(cue) * fade);
    }
  }

  /** Sheds one mark at the round's current place, dropping the oldest. */
  private pushMark(i: number, stride: number): void {
    const base = i * stride;
    let len = this.markLen[i];
    if (len === this.keep) {
      // The ledger is short (Terminal Burn keeps fifteen), so a shift is
      // one `copyWithin` and needs no ring head.
      this.marks.copyWithin(base, base + 3, base + this.keep * 3);
      len--;
    }
    const at = base + len * 3;
    this.marks[at] = this.x[i];
    this.marks[at + 1] = this.y[i];
    this.marks[at + 2] = this.travel[i];
    this.markLen[i] = len + 1;
  }

  /** The shed marks of round `i` as the style's own array shape. */
  private fillMarks(i: number, stride: number): Mark[] {
    const len = this.markLen[i];
    const base = i * stride;
    for (let j = 0; j < len; j++) {
      const m = this.markPool[j];
      m.x = this.marks[base + j * 3];
      m.y = this.marks[base + j * 3 + 1];
      m.travel = this.marks[base + j * 3 + 2];
      this.markView[j] = m;
    }
    // The pool holds every object, so trimming the window loses nothing and
    // the next round grows it back by assignment.
    this.markView.length = len;
    return this.markView;
  }

  private fillStage(i: number): { x: number; y: number; age: number } | null {
    if (!this.hasStage[i]) return null;
    this.stageView.x = this.stageX[i];
    this.stageView.y = this.stageY[i];
    this.stageView.age = this.stageAge[i];
    return this.stageView;
  }

  /**
   * Puts one round's pieces into the batch. `abs` pieces carry world
   * coordinates (a shed bubble, a spent first stage); the rest ride in the
   * round's own frame, so a style writes its shape once, nose to +x.
   */
  private place(i: number, pieces: Pieces, alphaK = 1): void {
    const heading = this.heading[i];
    const cos = Math.cos(heading), sin = Math.sin(heading);
    // The mark scale (R1981): a piece in the round's frame scales its
    // offset and its size; an `abs` piece keeps its place and scales its
    // size. At 1 every product is the figure itself.
    const m = this.markK;
    for (let k = 0; k < pieces.n; k++) {
      const p = pieces.list[k];
      const alpha = p.alpha * alphaK;
      if (alpha <= 0.004 || p.len <= 0 || p.wid <= 0) continue;
      const e = this.entry(p.tex);
      const ox = p.ox * m, oy = p.oy * m;
      const x = p.abs ? p.ox : this.x[i] + ox * cos - oy * sin;
      const y = p.abs ? p.oy : this.y[i] + ox * sin + oy * cos;
      const rot = p.abs ? p.rot : heading + p.rot;
      // The piece sets the full size, so the quad states `len` by `wid` and
      // takes only the texture rectangle from the atlas.
      this.quads.quad(x, y, 0, 0, p.len * m, p.wid * m, rot, 0, p.tint, alpha,
        e.u0, e.v0, e.u1, e.v1);
    }
  }

  /**
   * One quill pod (R2719, round 109 cards 09 to 12): the pod of its design
   * on its wave path, with the trail of its kind astern. It has no dart, no
   * streak and no heat ramp: it is no torpedo of a player. The wave rides
   * the run share of the wire, so the server's place stays the centre line.
   */
  private placePod(i: number): void {
    const design = this.pod[i], u = this.u[i], ph = (this.id[i] % 7) * 0.9;
    const h = this.heading[i], m = this.markK;
    const lat = podLateral(design, u, ph) * m;
    const nx = -Math.sin(h), ny = Math.cos(h);
    const pen = this.podPen, s = this.podState;
    s.t = this.podClock + (this.id[i] % 5) * 0.37;
    s.alpha = 1;
    pen.place(this.x[i] + nx * lat, this.y[i] + ny * lat, h, m);
    pen.ew = 0.6;
    pen.alpha = 1;
    drawPodTrail(design, pen, s, u, ph, POD_TRAIL_STEP_PX);
    const slope = Math.atan2(podLateral(design, Math.min(1, u + 0.01), ph) - podLateral(design, u, ph), 1);
    pen.push(0, 0, slope, 1);
    drawPod(design, pen, s);
    pen.pop();
  }

  private entry(tex: Piece['tex']): SpriteEntry {
    let e = this.texEntries.get(tex);
    if (!e) { e = this.sprite(torpedoKey(tex)); this.texEntries.set(tex, e); }
    return e;
  }

  private roundBuf: RoundSnap[] = [];

  /**
   * Snapshot of every round in flight, for the effects layer (R910, §6.6).
   *
   * The buffer and its objects are reused (R131): a reader must use them
   * inside the frame and must not keep the reference. `hidden` is the
   * answer the last `advance` came to, so the effects layer culls nothing
   * of its own.
   */
  rounds(): readonly RoundSnap[] {
    const out = this.roundBuf;
    for (let i = 0; i < this.n; i++) {
      let o = out[i];
      if (!o) {
        o = {
          id: 0, faction: 0, x: 0, y: 0, heading: 0, u: 0, speed: 0,
          kind: 0, effect: 0, hidden: false,
        };
        out[i] = o;
      }
      o.id = this.id[i];
      o.faction = this.faction[i];
      o.x = this.x[i]; o.y = this.y[i];
      o.heading = this.heading[i];
      o.u = this.u[i];
      o.speed = this.pxSec[i];
      o.kind = this.kind[i];
      o.effect = this.effect[i];
      o.hidden = this.hidden[i] === 1;
    }
    out.length = this.n;
    return out;
  }

  draw(ctx: DrawCtx): void {
    this.waitSegs.flush(ctx.cam);
    this.quads.flush(ctx.cam);
    this.waitDots.flush(ctx.cam);
    this.podSegs.flush(ctx.cam);
    this.podCircles.flush(ctx.cam);
  }
}
